import { PixModal } from "@/components/PixModal";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { BookOpen, PlaySquare, Layers, ArrowRight, ShoppingCart, Loader2, List, LayoutGrid, Clapperboard, Play } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { CourseQuotes, pickForCourse } from "@/components/Testimonials";
import { PresencialTag, isPresencial, periodo } from "@/components/Presencial";
import { CalendarDays, CheckCircle2, Gift, Award, Scissors } from "lucide-react";

const VIEW_KEY = "iep-courses-view";
const VIEWS = [
  { id: "grid", label: "Grade", icon: LayoutGrid },
  { id: "list", label: "Lista", icon: List },
  { id: "netflix", label: "Netflix", icon: Clapperboard },
];

function readView() {
  try {
    const saved = localStorage.getItem(VIEW_KEY);
    if (VIEWS.some((v) => v.id === saved)) return saved;
  } catch (e) {}
  return "grid";
}

const STATUS_LABEL = { completed: "Concluído", in_progress: "Em andamento", not_started: "" };
const STATUS_BADGE = { completed: "bg-emerald-100 text-emerald-700", in_progress: "bg-sky-100 text-sky-700", not_started: "" };

function formatDataBR(iso) {
  const [y, m, d] = (iso || "").split("-");
  return y ? `${d}/${m}/${y}` : "";
}

/** "Vitalício" quando não tem prazo; senão mostra a data e quantos dias faltam (some em vermelho perto do fim). */
function ExpiryInfo({ c, className = "" }) {
  if (typeof c.progress_percent !== "number") return null;
  if (!c.expires_at) {
    return <span className={`text-[11px] font-semibold text-[#1E9E2A] ${className}`}>Vitalício</span>;
  }
  const dias = c.days_remaining;
  const cor = dias == null ? "text-slate-400" : dias <= 7 ? "text-red-600" : dias <= 30 ? "text-amber-600" : "text-slate-400";
  const texto = dias == null ? `Expira em ${formatDataBR(c.expires_at)}`
    : dias < 0 ? "Acesso expirado"
    : dias === 0 ? "Expira hoje"
    : `Expira em ${formatDataBR(c.expires_at)} · ${dias} dia${dias === 1 ? "" : "s"}`;
  return <span className={`text-[11px] font-medium ${cor} ${className}`}>{texto}</span>;
}

/** Curso presencial: no lugar de progresso/"Começar", mostra a data da turma ou "Realizado". */
function PresencialStatus({ c, className = "", dark = false }) {
  if (c.presencial_done) return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${dark ? "bg-white text-[#1E9E2A]" : "bg-emerald-100 text-emerald-700"} ${className}`} data-testid="presencial-done"><CheckCircle2 className="h-3.5 w-3.5" /> Realizado</span>
  );
  const dias = periodo(c.event_date, c.event_end);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${dark ? "bg-white text-[#B45309]" : "bg-amber-100 text-[#B45309]"} ${className}`} data-testid="presencial-date"><CalendarDays className="h-3.5 w-3.5" /> {dias || "Data a definir"}</span>
  );
}

/** Curso online ganho de bônus na compra do presencial. */
function BonusTag({ c, className = "" }) {
  if (!c.bonus_of) return null;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold text-[#1E9E2A] ${className}`} data-testid="bonus-tag"><Gift className="h-3.5 w-3.5" /> Bônus online do curso presencial</span>;
}

function CourseThumb({ c, staff }) {
  return c.banner ? (
    <img src={c.banner} alt={c.title} loading="lazy" className="h-full w-full object-cover" />
  ) : (
    <div className="h-full w-full grid place-items-center" style={{ background: "linear-gradient(135deg,#232D4F,#1E9E2A)" }}>
      <BookOpen className="h-8 w-8 text-white/70" />
    </div>
  );
}

function ViewSwitcher({ view, setView }) {
  return (
    <div className="inline-flex items-center gap-1 bg-slate-100 rounded-lg p-1">
      {VIEWS.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          title={label}
          onClick={() => setView(id)}
          className={`p-1.5 rounded-md transition-colors ${view === id ? "bg-white shadow text-[#232D4F]" : "text-slate-400 hover:text-slate-600"}`}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
}

function GridCard({ c, staff, onOpen }) {
  const botaoLabel = c.progress_status === "completed" ? "Revisar" : c.progress_status === "in_progress" ? "Continuar" : "Começar";
  return (
    <button
      key={c.id}
      data-testid={`student-course-${c.id}`}
      onClick={onOpen}
      disabled={!onOpen} title={!onOpen ? "Curso presencial realizado (histórico)" : undefined}
      className={`text-left bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all overflow-hidden group flex flex-col disabled:cursor-default disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:scale-100 disabled:hover:shadow-sm`}
    >
      <div className="aspect-[3/2] bg-slate-100 relative overflow-hidden">
        {staff && c.published === false && <span className="absolute top-2 left-2 z-10 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Rascunho</span>}
        {c.progress_status && c.progress_status !== "not_started" && (
          <span className={`absolute top-2 left-2 z-10 text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[c.progress_status]}`}>{STATUS_LABEL[c.progress_status]}</span>
        )}
        <CourseThumb c={c} staff={staff} />
      </div>
      <div className="p-5 flex flex-col flex-1">
        {c.category && <span className="text-[10px] font-mono uppercase tracking-wider text-[#1E9E2A]">{c.category}</span>}
        <PresencialTag c={c} className="block" />
        <BonusTag c={c} className="block" />
        <h3 className="font-display font-semibold text-slate-800 mt-1 line-clamp-2">{c.title}</h3>
        <p className="text-sm text-slate-500 mt-1.5 line-clamp-2 flex-1">{c.description}</p>
        {typeof c.progress_percent === "number" && (
          <div className="mt-3">
            <Progress value={c.progress_percent} className="h-1.5" />
            <div className="flex items-center justify-between mt-1">
              <p className="text-[11px] text-slate-400">{c.progress_percent}% completo</p>
              <ExpiryInfo c={c} />
            </div>
          </div>
        )}
        {!staff && isPresencial(c) ? (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
            <span className="text-xs text-slate-500">{c.presencial_done ? "Histórico" : "Ver local e grade"}</span>
            <PresencialStatus c={c} />
          </div>
        ) : (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span className="flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> {c.module_count}</span>
            <span className="flex items-center gap-1"><PlaySquare className="h-3.5 w-3.5" /> {c.lesson_count}</span>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-[#1E9E2A] px-4 py-2 text-sm font-semibold text-white shadow-sm group-hover:gap-2.5 group-hover:bg-green-600 transition-all">
            {typeof c.progress_percent === "number" ? botaoLabel : "Acessar"} <ArrowRight className="h-4 w-4" />
          </span>
        </div>
        )}
      </div>
    </button>
  );
}

function ListRow({ c, staff, onOpen }) {
  const botaoLabel = c.progress_status === "completed" ? "Revisar" : c.progress_status === "in_progress" ? "Continuar" : "Começar";
  return (
    <button
      key={c.id}
      data-testid={`student-course-${c.id}`}
      onClick={onOpen}
      disabled={!onOpen} title={!onOpen ? "Curso presencial realizado (histórico)" : undefined}
      className={`w-full text-left bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all overflow-hidden flex items-center gap-4 p-3 disabled:cursor-default disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:scale-100 disabled:hover:shadow-sm`}
    >
      <div className="w-28 sm:w-36 aspect-video bg-slate-100 rounded-lg overflow-hidden flex-shrink-0 relative">
        {staff && c.published === false && <span className="absolute top-1 left-1 z-10 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">Rascunho</span>}
        <CourseThumb c={c} staff={staff} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-display font-semibold text-slate-800 line-clamp-1">{c.title}</h3>
          {c.progress_status && c.progress_status !== "not_started" && (
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${STATUS_BADGE[c.progress_status]}`}>{STATUS_LABEL[c.progress_status]}</span>
          )}
        </div>
        {c.coordinator_name && <p className="text-xs text-slate-400 mt-0.5">{c.coordinator_name}</p>}
        <PresencialTag c={c} className="block mt-0.5" />
        <BonusTag c={c} className="block mt-0.5" />
        {typeof c.progress_percent === "number" && (
          <div className="mt-2 max-w-xs">
            <Progress value={c.progress_percent} className="h-1.5" />
          </div>
        )}
        <ExpiryInfo c={c} className="block mt-1" />
      </div>
      <div className="flex-shrink-0 flex items-center gap-3">
        {!staff && isPresencial(c) ? <PresencialStatus c={c} /> : (<>
        {typeof c.progress_percent === "number" && <span className="text-xs text-slate-400 hidden sm:inline">{c.progress_percent}%</span>}
        <Button className="text-white font-semibold" style={{ background: "#1E9E2A" }}>{typeof c.progress_percent === "number" ? botaoLabel : "Acessar"}</Button>
        </>)}
      </div>
    </button>
  );
}

function NetflixTile({ c, staff, onOpen }) {
  const botaoLabel = c.progress_status === "completed" ? "Revisar" : c.progress_status === "in_progress" ? "Continuar" : "Começar";
  return (
    <button
      key={c.id}
      data-testid={`student-course-${c.id}`}
      onClick={onOpen}
      disabled={!onOpen} title={!onOpen ? "Curso presencial realizado (histórico)" : undefined}
      className={`relative text-left aspect-video bg-slate-800 rounded-lg overflow-hidden group transition-transform duration-200 hover:scale-105 hover:z-10 hover:shadow-2xl disabled:cursor-default disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:scale-100 disabled:hover:shadow-sm`}
    >
      {staff && c.published === false && <span className="absolute top-1.5 left-1.5 z-20 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">Rascunho</span>}
      <CourseThumb c={c} staff={staff} />
      {typeof c.progress_percent === "number" && c.progress_percent > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40">
          <div className="h-full bg-[#1E9E2A]" style={{ width: `${c.progress_percent}%` }} />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-end p-3">
        <h3 className="font-display font-semibold text-white text-sm line-clamp-2">{c.title}</h3>
        {c.progress_status && c.progress_status !== "not_started" && (
          <p className="text-[11px] text-white/70 mt-0.5">{STATUS_LABEL[c.progress_status]}{typeof c.progress_percent === "number" ? ` · ${c.progress_percent}%` : ""}</p>
        )}
        {c.bonus_of && <p className="text-[11px] text-emerald-300 mt-0.5">🎁 Bônus do curso presencial</p>}
        <ExpiryInfo c={c} className="block mt-0.5" />
        {!staff && isPresencial(c) ? <PresencialStatus c={c} dark className="mt-2 w-fit" /> : (
        <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#232D4F] bg-white rounded-full px-3 py-1.5 w-fit">
          <Play className="h-3.5 w-3.5" /> {typeof c.progress_percent === "number" ? botaoLabel : "Acessar"}
        </span>
        )}
      </div>
    </button>
  );
}

/** Catálogo do aluno; professor/mentora usa o mesmo (base="/prof", staff) e vê todos os cursos. */
export default function Catalog({ base = "/app", staff = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState(null);
  const [shop, setShop] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [buying, setBuying] = useState("");
  const [view, setView] = useState(readView);
  const [certs, setCerts] = useState([]);     // certificados prontos para emitir (inclusive de curso com acesso vencido)

  useEffect(() => {
    // professora: a tela de cursos é para assistir/demonstrar; curso presencial (sem aulas) fica só nos Links de venda
    api.get("/courses", { params: staff ? { all: 1 } : {} }).then((r) => setCourses(staff ? r.data.filter((c) => c.modality !== "presencial") : r.data)).catch(() => setCourses([]));
    if (!staff) api.get("/shop/courses").then((r) => setShop(r.data)).catch(() => {});
    if (!staff) api.get("/site").then((r) => setTestimonials(r.data?.testimonials || [])).catch(() => {});
    if (!staff) api.get("/me/certificates/disponiveis").then((r) => setCerts(r.data.filter((c) => c.pode_emitir))).catch(() => {});
  }, [staff]);

  // presencial "Realizado" fica só como histórico: não abre mais (evita confundir com o curso online)
  const abrir = (c) => (!staff && c.presencial_done ? undefined : () => navigate(`${base}/curso/${c.id}`));

  const trocarView = (v) => {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch (e) {}
  };

  const [cpfAsk, setCpfAsk] = useState(null);    // { courseId } quando falta o CPF para a nota fiscal
  const [cpfVal, setCpfVal] = useState("");
  const [cpfBusy, setCpfBusy] = useState(false);
  const [pix, setPix] = useState(null);       // { order_id, pix: { qr_code, qr_image, amount } } enquanto o Pix aguarda pagamento
  const buy = async (courseId, method = "", combo = false) => {
    setBuying(courseId + method);
    try {
      const res = await api.post("/payments/mercadopago/preference", combo ? { combo_id: courseId, method } : { course_id: courseId, method });
      if (res.data.pix) { setPix(res.data); setBuying(""); return; }
      window.location.assign(res.data.init_point);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (typeof d === "string" && d.startsWith("CPF_OBRIGATORIO")) { setCpfAsk({ courseId, method, combo }); setBuying(""); return; }
      toast.error(formatApiError(d));
      setBuying("");
    }
  };
  const salvarCpf = async () => {
    setCpfBusy(true);
    try { await api.post("/me/fiscal", { document: cpfVal }); const { courseId: id, method: mt, combo: cb } = cpfAsk; setCpfAsk(null); setCpfVal(""); await buy(id, mt, cb); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setCpfBusy(false); }
  };

  return (
    <main className="max-w-6xl mx-auto w-full px-5 md:px-8 py-8">
      <PixModal data={pix} statusUrl={pix ? `/payments/order/${pix.order_id}` : ""} loggedIn onClose={() => setPix(null)} />
      {cpfAsk && (
        <div className="fixed inset-0 z-50 bg-black/45 grid place-items-center p-4" data-testid="cpf-dialog">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4">
            <h3 className="font-display text-xl font-bold text-slate-900">Informe seu CPF</h3>
            <p className="text-sm text-slate-600">Para concluir a compra precisamos do seu CPF: ele é exigido para emitir a nota fiscal do curso. Você informa uma vez só.</p>
            <input value={cpfVal} onChange={(e) => setCpfVal(e.target.value)} inputMode="numeric" placeholder="000.000.000-00" className="h-11 w-full rounded-md border border-slate-200 px-3 text-base" data-testid="cpf-input" autoFocus />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setCpfAsk(null); setCpfVal(""); }} className="px-4 h-10 rounded-md border border-slate-200 text-sm">Cancelar</button>
              <button onClick={salvarCpf} disabled={cpfBusy || cpfVal.replace(/\D/g, "").length < 11} className="px-4 h-10 rounded-md text-white text-sm font-semibold disabled:opacity-50" style={{ background: "#1E9E2A" }} data-testid="cpf-save">{cpfBusy ? "Salvando…" : "Continuar para o pagamento"}</button>
            </div>
          </div>
        </div>
      )}
      {user?.salao && (
        <a href={`${API}/sso/salao`} className="mb-6 flex items-center gap-4 rounded-2xl p-4 sm:p-5 text-white shadow-md hover:shadow-lg transition-shadow" style={{ background: "linear-gradient(135deg,#232D4F,#3A2F1E 70%,#926B38)" }} data-testid="atalho-salao">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-white/95"><Scissors className="h-7 w-7" style={{ color: "#926B38" }} /></span>
          <span className="flex-1 min-w-0">
            <span className="block font-display text-lg font-bold">IEP Salão</span>
            <span className="block text-sm text-white/80">Agenda, clientes, caixa e agendamento online do seu salão.</span>
          </span>
          <span className="shrink-0 rounded-lg bg-white px-4 py-2 text-sm font-semibold" style={{ color: "#232D4F" }}>Abrir meu salão</span>
        </a>
      )}
      {user?.tricolab && (
        <a href="/tricolab" target="_blank" rel="noopener" className="mb-6 flex items-center gap-4 rounded-2xl p-4 sm:p-5 text-white shadow-md hover:shadow-lg transition-shadow" style={{ background: "linear-gradient(135deg,#0f1f1a,#1d3a31 70%,#2f8f63)" }} data-testid="atalho-tricolab">
          <img src="/brand/tricolab/icon-192.png" alt="" className="h-14 w-14 shrink-0 rounded-xl" />
          <span className="flex-1 min-w-0">
            <span className="block font-display text-lg font-bold tracking-wide">Tricolab</span>
            <span className="block text-sm text-white/80">Check-up capilar: os 7 testes, a interpretação e a fórmula da sua cliente.</span>
          </span>
          <span className="shrink-0 rounded-lg bg-white px-4 py-2 text-sm font-semibold" style={{ color: "#0f1f1a" }}>Abrir o Tricolab</span>
        </a>
      )}
      {certs.length > 0 && (
        <div className="mb-6 rounded-2xl border border-green-200 bg-green-50 p-4 sm:p-5 space-y-3" data-testid="certs-disponiveis">
          {certs.map((c) => (
            <div key={c.course_id} className="flex items-center gap-3 flex-wrap">
              <Award className="h-8 w-8 text-[#1E9E2A] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800">Seu certificado está pronto!</div>
                <div className="text-sm text-slate-600">{c.course_title}</div>
              </div>
              <Button onClick={() => navigate(`${base}/certificado/${c.course_id}`)} className="bg-[#1E9E2A] hover:bg-green-700 text-white">Emitir certificado</Button>
            </div>
          ))}
        </div>
      )}
      {/* Hero: mesmo visual da tela de entrada (frascos, a mulher e os produtos IEP) */}
      <div className="aluna-hero relative rounded-3xl overflow-hidden mb-8 animate-fade-up">
        <img src="/brand/login-arte-frascos.webp" alt="" className="aluna-hero-frascos" />
        <img src="/brand/aluna-hero-arte.webp" alt="" className="aluna-hero-mulher" />
        <div className="relative px-6 py-7 sm:py-10 sm:pl-[118px] sm:pr-10 max-w-2xl">
          <div className="hidden sm:block text-[11px] tracking-[4px] font-bold text-[#232D4F]">CIÊNCIA • FORMULAÇÃO • GESTÃO • RESULTADO</div>
          <p className="text-[#1E9E2A] text-sm font-bold sm:mt-3">Olá, {user?.name?.split(" ")[0]} 👋</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#1A2340] mt-1 max-w-xl">{staff ? "Sala do Professor" : "Continue aprendendo"}</h1>
          <p className="text-slate-600 mt-2 max-w-lg">{staff ? "Acompanhe seus cursos e apresente as aulas pra suas turmas." : "Explore os cursos disponíveis do IEP e evolua na sua carreira profissional."}</p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-[#232D4F]" />
          <h2 className="font-display text-xl font-bold text-slate-900">{staff ? "Cursos" : "Meus cursos"}</h2>
        </div>
        {courses && courses.length > 0 && <ViewSwitcher view={view} setView={trocarView} />}
      </div>

      {courses === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-xl" />)}
        </div>
      ) : courses.length === 0 ? (
        <div className="p-12 text-center rounded-2xl border-2 border-dashed border-slate-200 bg-white">
          <BookOpen className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Nenhum curso disponível no momento.</p>
        </div>
      ) : view === "list" ? (
        <div className="flex flex-col gap-3">
          {courses.map((c) => <ListRow key={c.id} c={c} staff={staff} onOpen={abrir(c)} />)}
        </div>
      ) : view === "netflix" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {courses.map((c) => <NetflixTile key={c.id} c={c} staff={staff} onOpen={abrir(c)} />)}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {courses.map((c) => <GridCard key={c.id} c={c} staff={staff} onOpen={abrir(c)} />)}
        </div>
      )}

      {shop.length > 0 && (
        <section className="mt-14">
          <div className="flex items-center gap-2 mb-5">
            <ShoppingCart className="h-5 w-5 text-[#1E9E2A]" />
            <h2 className="font-display text-xl font-bold text-slate-900">Disponíveis para compra</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {shop.map((c) => (
              <div key={c.id} data-testid={`shop-course-${c.id}`} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                <div className="aspect-[3/2] bg-slate-100 relative overflow-hidden">
                  {c.banner ? <img src={c.banner} alt={c.title} loading="lazy" className="h-full w-full object-cover" /> : (
                    <div className="h-full w-full grid place-items-center" style={{ background: "linear-gradient(135deg,#232D4F,#1E9E2A)" }}><BookOpen className="h-8 w-8 text-white/70" /></div>
                  )}
                  <span className="absolute top-2 right-2 bg-white rounded-lg shadow px-2.5 py-1 text-right leading-tight" data-testid={`shop-price-${c.id}`}>
                    {c.promo_price > 0 ? (
                      <>
                        <span className="block text-[11px] text-slate-400 line-through">R$ {c.price.toFixed(2).replace(".", ",")} no cartão</span>
                        <span className="block font-bold text-base text-[#1E9E2A]">R$ {c.promo_price.toFixed(2).replace(".", ",")} <span className="text-[11px] font-semibold">no Pix</span></span>
                      </>
                    ) : <span className="font-bold text-sm text-[#232D4F]">R$ {c.price.toFixed(2).replace(".", ",")}</span>}
                  </span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  {c.category && <span className="text-[10px] font-mono uppercase tracking-wider text-[#1E9E2A]">{c.category}</span>}
                  <PresencialTag c={c} className="block" />
                  <h3 className="font-display font-semibold text-slate-800 mt-1 line-clamp-2">{c.title}</h3>
                  <p className="text-sm text-slate-500 mt-1.5 line-clamp-2 flex-1">{c.description}</p>
                  {c.kind === "combo" ? (
                    <ul className="mt-3 text-xs text-slate-600 space-y-0.5" data-testid={`combo-includes-${c.id}`}>{c.courses.map((x) => <li key={x.id}>✓ {x.title}</li>)}
                      <li className="pt-1 text-[#1E9E2A] font-semibold">Separados somam R$ {c.sum_separate.toFixed(2).replace(".", ",")}</li></ul>
                  ) : <CourseQuotes items={pickForCourse(testimonials, c.id, { max: 2, maxLen: 260 })} className="mt-3" />}
                  {c.promo_price > 0 ? (
                    <div className="mt-4 space-y-2">
                      <Button data-testid={`buy-pix-${c.id}`} onClick={() => buy(c.id, "pix", c.kind === "combo")} disabled={!!buying} className="w-full text-white font-semibold" style={{ background: "#1E9E2A" }}>
                        {buying === c.id + "pix" ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Pagar no Pix · R$ {c.promo_price.toFixed(2).replace(".", ",")}</>}
                      </Button>
                      <Button data-testid={`buy-card-${c.id}`} variant="outline" onClick={() => buy(c.id, "card", c.kind === "combo")} disabled={!!buying} className="w-full font-semibold">
                        {buying === c.id + "card" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShoppingCart className="h-4 w-4 mr-1.5" /> Pagar no cartão · R$ {c.price.toFixed(2).replace(".", ",")}</>}
                      </Button>
                    </div>
                  ) : (
                    <Button data-testid={`buy-course-${c.id}`} onClick={() => buy(c.id, "", c.kind === "combo")} disabled={!!buying} className="mt-4 w-full text-white font-semibold" style={{ background: "#1E9E2A" }}>
                      {buying === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShoppingCart className="h-4 w-4 mr-1.5" /> {c.kind === "combo" ? "Comprar combo" : "Comprar curso"}</>}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
